import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Search, SlidersHorizontal } from 'lucide-react';

import type { ReferenceRead } from '@/api/schema';
import { Toolbar } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { Combobox } from '@/components/ui/Combobox';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useDebounced } from '@/lib/useDebounced';

import {
  useCandidateDepartments,
  useCountries,
  usePositions,
  type CandidateFilters as Filters,
} from '../api';
import {
  APPLYING_FOR_OPTIONS,
  DOCUMENTATION_OPTIONS,
  FLYST_OPTIONS,
  PAYMENT_OPTIONS,
  RSR_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
} from '../labels';
import { MAX_SEARCH_LENGTH, hasAdvancedFilter, isFiltered } from '../urlState';
import styles from './CandidateFilters.module.css';

const POINTS_OPTIONS = ['0', '1', '2', '3', '4', '5'].map((value) => ({ value, label: value }));

const CONTACTED_OPTIONS = [
  { value: 'true', label: 'Contacted' },
  { value: 'false', label: 'Not contacted' },
];

function referenceOptions(list: ReferenceRead[] | undefined) {
  return (list ?? []).map((item) => ({ value: item.id, label: item.name }));
}

/** How many of the filters behind the disclosure are actually set. */
function advancedCount(filters: Filters): number {
  return [
    filters.applying_for,
    filters.preferred_country_id,
    filters.preferred_position_id,
    filters.flyst_stage,
    filters.rsr_state,
    filters.documentation,
    filters.payment_status,
    filters.contacted,
    filters.received_from,
    filters.received_to,
    filters.points_min,
    filters.points_max,
  ].filter((value) => value !== '').length;
}

export function CandidateFilters({
  filters,
  onChange,
  onClear,
  trailing,
}: {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
  onClear: () => void;
  /** Rows-per-page and the column chooser, pushed to the far end of the toolbar. */
  trailing: ReactNode;
}) {
  const departments = useCandidateDepartments();
  const countries = useCountries();
  const positions = usePositions();

  /*
   * The search box is the one control with local state, because the raw value has to
   * drive the input (waiting for a URL round-trip per keystroke would feel broken)
   * while only the settled value reaches the URL.
   *
   * That makes it a mirror, and a mirror has to be two-way. It was one-way at first,
   * and the URL then moved without the box noticing: pressing Back after clearing, or
   * clicking "All candidates" in the sidebar, left a term sitting in the box over an
   * unfiltered list, or an empty box over a filtered one.
   *
   * `pushed` is what this component last sent to the URL. When `filters.search` comes
   * back as that value, the change is our own echo and the draft is already right.
   * When it comes back as anything else, somebody moved the URL from outside and the
   * box adopts it. Tracking the pushed value is what stops a fast typist being
   * clobbered: mid-flight the URL settles on an earlier term, and comparing against
   * the URL alone would overwrite the letters typed since.
   */
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const pushed = useRef(filters.search);
  const debouncedSearch = useDebounced(searchDraft, 300);

  // Out: the settled term goes to the URL, and is recorded as ours.
  useEffect(() => {
    if (debouncedSearch === pushed.current) return;
    pushed.current = debouncedSearch;
    onChange({ search: debouncedSearch });
    // `onChange` is a new closure on every render and would re-fire this on each one.
    // The settled term is the only thing that should cause a write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // In: a URL that moved on its own gets adopted. Recording the push above is what
  // makes this safe for a fast typist. While "john" is in flight the box may already
  // read "johnx", and comparing against the URL alone would overwrite the extra
  // letters the moment the earlier term landed.
  //
  // This is the textbook use for an effect: keeping local state in step with an
  // external system, which the browser's history is.
  useEffect(() => {
    if (filters.search === pushed.current) return;
    pushed.current = filters.search;
    setSearchDraft(filters.search);
  }, [filters.search]);

  // Open on arrival when a shared link carries an advanced filter, so a filter is
  // never active but hidden. A disclosure, not a preference, so it is not persisted.
  const [more, setMore] = useState(() => hasAdvancedFilter(filters));

  const advanced = advancedCount(filters);

  return (
    <>
      <Toolbar>
        <div className={styles.search}>
          <Input
            icon={<Search size={15} />}
            // Exactly what the server searches. A placeholder promising a field the
            // API does not match produces silent empty results that look like missing
            // data.
            placeholder="Search name, email, phone or reference"
            aria-label="Search candidates"
            // The server answers a longer term with a 422, which would replace the
            // register with an error screen for a pasted paragraph.
            maxLength={MAX_SEARCH_LENGTH}
            value={searchDraft}
            onChange={(event) => {
              setSearchDraft(event.target.value);
            }}
          />
        </div>

        <Select
          placeholder="All statuses"
          aria-label="Filter by status"
          value={filters.status}
          options={STATUS_OPTIONS}
          onChange={(event) => {
            onChange({ status: event.target.value as Filters['status'] });
          }}
        />

        <Select
          placeholder="All departments"
          aria-label="Filter by department"
          value={filters.department_id}
          options={referenceOptions(departments.data)}
          onChange={(event) => {
            onChange({ department_id: event.target.value });
          }}
        />

        <Select
          placeholder="All sources"
          aria-label="Filter by source"
          value={filters.source}
          options={SOURCE_OPTIONS}
          onChange={(event) => {
            onChange({ source: event.target.value as Filters['source'] });
          }}
        />

        {/* No local reset needed: clearing empties `filters.search`, and the sync
            above adopts it. That is also why the "Clear filters" button in the empty
            state now works, though it never touched this component. */}
        {isFiltered(filters) ? (
          <Button variant="ghost" size="sm" onClick={onClear}>
            Clear
          </Button>
        ) : null}

        <Button
          variant="ghost"
          size="sm"
          icon={<SlidersHorizontal size={14} />}
          aria-expanded={more}
          onClick={() => {
            setMore((current) => !current);
          }}
        >
          More filters{advanced > 0 ? ` (${advanced})` : ''}
        </Button>

        <div className={styles.trailing}>{trailing}</div>
      </Toolbar>

      {more ? (
        <div className={styles.more}>
          <Select
            placeholder="All types"
            aria-label="Filter by application type"
            value={filters.applying_for}
            options={APPLYING_FOR_OPTIONS}
            onChange={(event) => {
              onChange({ applying_for: event.target.value as Filters['applying_for'] });
            }}
          />

          {/* Comboboxes, not Selects: these two lists are long enough that scrolling a
              native dropdown is the wrong way to find "Portugal". */}
          <div className={styles.wide}>
            <Combobox
              placeholder="Any country"
              value={filters.preferred_country_id}
              options={referenceOptions(countries.data)}
              onChange={(value) => {
                onChange({ preferred_country_id: value });
              }}
            />
          </div>

          <div className={styles.wide}>
            <Combobox
              placeholder="Any position"
              value={filters.preferred_position_id}
              options={referenceOptions(positions.data)}
              onChange={(value) => {
                onChange({ preferred_position_id: value });
              }}
            />
          </div>

          <Select
            placeholder="Any Flyst stage"
            aria-label="Filter by Flyst stage"
            value={filters.flyst_stage}
            options={FLYST_OPTIONS}
            onChange={(event) => {
              onChange({ flyst_stage: event.target.value as Filters['flyst_stage'] });
            }}
          />

          <Select
            placeholder="Any RSR state"
            aria-label="Filter by RSR state"
            value={filters.rsr_state}
            options={RSR_OPTIONS}
            onChange={(event) => {
              onChange({ rsr_state: event.target.value as Filters['rsr_state'] });
            }}
          />

          <Select
            placeholder="Any documentation"
            aria-label="Filter by documentation"
            value={filters.documentation}
            options={DOCUMENTATION_OPTIONS}
            onChange={(event) => {
              onChange({ documentation: event.target.value as Filters['documentation'] });
            }}
          />

          <Select
            placeholder="Any payment status"
            aria-label="Filter by payment status"
            value={filters.payment_status}
            options={PAYMENT_OPTIONS}
            onChange={(event) => {
              onChange({ payment_status: event.target.value as Filters['payment_status'] });
            }}
          />

          <Select
            placeholder="Contacted: any"
            aria-label="Filter by contacted"
            value={filters.contacted}
            options={CONTACTED_OPTIONS}
            onChange={(event) => {
              onChange({ contacted: event.target.value as Filters['contacted'] });
            }}
          />

          <Input
            type="date"
            aria-label="Received from"
            value={filters.received_from}
            onChange={(event) => {
              onChange({ received_from: event.target.value });
            }}
          />

          <Input
            type="date"
            aria-label="Received to"
            value={filters.received_to}
            onChange={(event) => {
              onChange({ received_to: event.target.value });
            }}
          />

          {/* Selects rather than number inputs: the range is 0 to 5 and the API
              answers anything outside it with a 422. */}
          <Select
            placeholder="Min points"
            aria-label="Minimum points"
            value={filters.points_min}
            options={POINTS_OPTIONS}
            onChange={(event) => {
              onChange({ points_min: event.target.value });
            }}
          />

          <Select
            placeholder="Max points"
            aria-label="Maximum points"
            value={filters.points_max}
            options={POINTS_OPTIONS}
            onChange={(event) => {
              onChange({ points_max: event.target.value });
            }}
          />
        </div>
      ) : null}
    </>
  );
}
